import { useEffect, useState } from 'react';
import { AirKey } from 'airkey-mfa-react';
import { IPhoneScreen } from '@/components/3ds/IPhoneScreen/IPhoneScreen';
import { FullScreenLoader } from '@/components/LoadScreen/FullScreenLoader';
import { WebNFCChecker } from '@/components/WebNFCChecker/WebNFCChecker';
import { useDevice } from '@/hooks/useDevice/useDevice';
import { BrowserNotSupported } from '@/pages/DeviceError/BrowserNotSupported';
import { DeviceError } from '@/pages/DeviceError/DeviceError';
import { NFCNotEnabled } from '@/pages/DeviceError/NFCNotEnabled';
import { PrivateBrowserError } from '@/pages/DeviceError/PrivateBrowserError';
import { SafariRequired } from '@/pages/DeviceError/SafariRequired';
import { useAirKeyTrace } from '@/utils/airKeyTrace';

const IS_DEV = !!import.meta.env.DEV;
const IS_TEST = !!import.meta.env.TEST;
AirKey.setSimulateNfc(IS_DEV || IS_TEST);

// name format taken from https://github.com/bowser-js/bowser/blob/master/src/constants.js#L3
const COMPATIBLE_BROWSERS = ['Chrome', 'Microsoft Edge'];

/**
 * Wrapper component that either shows an error, loads the native app, or permits the app to continue the flow in web.
 */
export function DeviceCompatibilityWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const currentUrl = window.location.href;
  const device = useDevice();
  const sendTrace = useAirKeyTrace();
  const [deviceInitialized, setDeviceInitialized] = useState(false);
  const {
    hasNDEFReader,
    nfcEnabled,
    isApple,
    isPrivateMode,
    isMobile,
    isSafari,
    isValidIOSVersion,
    loading,
    browserName,
  } = device;

  useEffect(() => {
    if (!loading && !deviceInitialized) {
      sendTrace({
        view: 'loading',
        action: 'system_initialized',
        additionalData: {
          component: 'DeviceCompatibilityWrapper',
          has_ndef_reader: hasNDEFReader,
          nfc_enabled: nfcEnabled,
          is_apple: isApple,
          is_private_mode: isPrivateMode,
          is_mobile: isMobile,
          is_safari: isSafari,
          is_valid_ios_version: isValidIOSVersion,
          browser_name: browserName,
        },
      });
      setDeviceInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  // Note: device and sendTrace omitted intentionally - only send event once when device detection completes

  if (loading) {
    return <FullScreenLoader />;
  }

  if (isApple) {
    if (!isMobile) {
      return <DeviceError />;
    }

    if (isPrivateMode) {
      return <PrivateBrowserError iOS link={currentUrl} />;
    }

    if (!isSafari) {
      return <SafariRequired link={currentUrl} />;
    }

    if (!isValidIOSVersion) {
      // to be replaced with a link to the iOS version error page
      // return <IOSVersionError link={currentUrl} />;
      return <DeviceError />;
    }

    return <IPhoneScreen />;
  }

  if (isPrivateMode) {
    return <PrivateBrowserError link={currentUrl} />;
  }

  if (isMobile && !COMPATIBLE_BROWSERS.includes(browserName)) {
    return <BrowserNotSupported link={currentUrl} />;
  }

  if (hasNDEFReader) {
    if (nfcEnabled) {
      return (
        <WebNFCChecker mockEnableNfc={IS_DEV || IS_TEST}>
          {children}
        </WebNFCChecker>
      );
    }
    return <NFCNotEnabled />;
  }

  // user is on mobile, not ios, and using a known, compatible browser in a public tab
  let mockEnableNfc = IS_DEV || IS_TEST;

  try {
    const testingHarness = localStorage.getItem('testingHarness');
    if (testingHarness && (IS_DEV || IS_TEST)) {
      const parsed = JSON.parse(testingHarness);
      mockEnableNfc = parsed.mockEnableNfc ?? mockEnableNfc;
    }
  } catch (e) {
    console.error('Failed to parse testingHarness:', e);
  }

  return (
    <WebNFCChecker mockEnableNfc={mockEnableNfc}>{children}</WebNFCChecker>
  );
}
